import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { isSession } from '../../services/api';
import { TariffTier, errorMessage, priceForQuantity, useFetch } from '../../services/operatorApi';
import { Badge, Button, Card, Empty, ErrorBox, PageHeader, Spinner, StatCard, Table, Td, dateTime, eur, inputClass, num } from '../../components/ui';

interface Tariff {
  tiers: TariffTier[];
  vat_percent: number;
  payments_enabled: boolean;
  min_quantity: number;
  max_quantity: number;
}
interface Credits {
  billing_mode: 'free' | 'prepaid';
  balance: number;
  committed: number;
  available: number;
}
interface Order {
  id: string;
  quantity: number;
  amount_ht_cents: number;
  vat_cents: number | null;
  total_cents: number | null;
  invoice_url: string | null;
  invoice_pdf: string | null;
  paid_at: string;
}
interface LedgerRow {
  id: string;
  kind: 'purchase' | 'grant' | 'adjustment';
  credits: number;
  amount_cents: number | null;
  reference: string | null;
  note: string | null;
  created_at: string;
}

const KIND: Record<LedgerRow['kind'], string> = { purchase: 'Achat de lot', grant: 'Offert', adjustment: 'Correction' };
const PRESETS = [20, 50, 100, 250, 500];

export default function BillingPage() {
  const session = isSession();
  const [params] = useSearchParams();
  const { data, error, loading, reload } = useFetch(async () => {
    const [tariff, credits, billing] = await Promise.all([
      api.get<Tariff>('/public/tariff'),
      api.get<Credits>('/credits'),
      session ? api.get<{ orders: Order[]; ledger: LedgerRow[] }>('/account/billing') : Promise.resolve(null),
    ]);
    return { tariff: tariff.data, credits: credits.data, orders: billing?.data.orders ?? [], ledger: billing?.data.ledger ?? [] };
  });
  const [quantity, setQuantity] = useState(50);
  const [paying, setPaying] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  if (loading && !data) return <Spinner />;
  if (error || !data) return <ErrorBox message={error ?? 'Chargement impossible'} onRetry={reload} />;

  const { tariff, credits, orders, ledger } = data;
  const qty = Math.max(0, Math.min(tariff.max_quantity, Math.floor(quantity) || 0));
  const ht = priceForQuantity(tariff.tiers, qty);
  const vat = Math.round((ht * tariff.vat_percent) / 100);
  const canBuy = session && tariff.payments_enabled && qty >= tariff.min_quantity;

  async function buy() {
    setPaying(true);
    setFailure(null);
    try {
      window.location.href = (await api.post<{ url: string }>('/account/checkout', { quantity: qty })).data.url;
    } catch (e) {
      setFailure(errorMessage(e));
      setPaying(false);
    }
  }

  return (
    <div>
      <PageHeader title="Facturation" subtitle="Votre solde de signatures, l'achat d'un nouveau lot et vos factures" />

      {params.get('paid') === '1' && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <span>Merci, votre paiement est en cours de validation. Le solde est mis à jour en quelques secondes et la facture vous est envoyée par e-mail.</span>
          <Button variant="secondary" small onClick={reload}>Actualiser</Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Signatures disponibles" value={num(credits.available)} tone={credits.available <= 0 ? 'bad' : credits.available <= 10 ? 'warn' : 'good'} />
        <StatCard label="Au solde" value={num(credits.balance)} />
        <StatCard label="Attendues (documents en cours)" value={num(credits.committed)} />
      </div>

      <Card title="Acheter un lot" className="mt-6">
        {!session ? (
          <Empty>L&apos;achat en ligne demande une connexion par e-mail : déconnectez-vous puis choisissez « Recevoir un code ».</Empty>
        ) : !tariff.payments_enabled ? (
          <Empty>L&apos;achat en ligne n&apos;est pas disponible pour le moment. Contactez-nous pour recharger votre compte.</Empty>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <div className="mb-3 flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <button key={p} type="button" onClick={() => setQuantity(p)} className={`rounded-full px-3.5 py-1 text-sm font-medium ring-1 ${qty === p ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-slate-700 ring-slate-300 hover:ring-indigo-400'}`}>{p}</button>
                ))}
              </div>
              <label className="block text-sm font-medium text-slate-700">
                Nombre de signatures
                <input type="number" min={tariff.min_quantity} max={tariff.max_quantity} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} className={`${inputClass} mt-1`} />
              </label>
              <ul className="mt-3 space-y-1 text-sm text-slate-600">
                {tariff.tiers.map((tier, i) => {
                  const from = i === 0 ? 1 : (tariff.tiers[i - 1].up_to ?? 0) + 1;
                  return <li key={i}>{tier.up_to === null ? `Dès ${from} signatures` : `${from} à ${tier.up_to} signatures`} : {eur(tier.unit_cents)} HT l&apos;unité, pour tout le lot</li>;
                })}
              </ul>
            </div>
            <div className="rounded-xl bg-slate-900 p-5 text-white">
              <p className="text-3xl font-bold tabular-nums">{eur(ht)} <span className="text-sm font-normal text-slate-400">HT</span></p>
              <p className="mt-1 text-sm text-slate-300">{qty > 0 ? `soit ${eur(ht / qty)} HT par signature` : 'Saisissez une quantité'}</p>
              <p className="mt-3 border-t border-slate-700 pt-3 text-sm text-slate-300">TVA {tariff.vat_percent} % : {eur(vat)} · Total TTC : <strong className="text-white">{eur(ht + vat)}</strong></p>
              {failure && <p className="mt-3 text-sm text-red-300">{failure}</p>}
              <button onClick={buy} disabled={!canBuy || paying} className="mt-4 w-full rounded-lg bg-indigo-500 py-2.5 text-sm font-semibold text-white hover:bg-indigo-400 disabled:opacity-50">
                {paying ? 'Redirection…' : qty < tariff.min_quantity ? `Minimum ${tariff.min_quantity} signatures` : `Payer ${eur(ht + vat)} TTC`}
              </button>
              <p className="mt-2 text-center text-xs text-slate-400">Paiement sécurisé par carte (Stripe). Facture envoyée par e-mail.</p>
            </div>
          </div>
        )}
      </Card>

      <Card title="Factures" className="mt-6">
        {!session ? (
          <Empty>Connectez-vous avec votre e-mail pour retrouver vos factures.</Empty>
        ) : orders.length === 0 ? (
          <Empty>Aucune facture pour le moment.</Empty>
        ) : (
          <Table head={['Date', 'Lot', 'HT', 'TVA', 'TTC', 'Facture']}>
            {orders.map((o) => (
              <tr key={o.id}>
                <Td className="whitespace-nowrap">{dateTime(o.paid_at)}</Td>
                <Td>{num(o.quantity)} signatures</Td>
                <Td className="tabular-nums">{eur(o.amount_ht_cents)}</Td>
                <Td className="tabular-nums">{o.vat_cents === null ? '—' : eur(o.vat_cents)}</Td>
                <Td className="tabular-nums">{o.total_cents === null ? '—' : eur(o.total_cents)}</Td>
                <Td>
                  {o.invoice_url ? <a className="text-indigo-600 hover:underline" href={o.invoice_url} target="_blank" rel="noreferrer">Voir</a> : '—'}
                  {o.invoice_pdf && <> · <a className="text-indigo-600 hover:underline" href={o.invoice_pdf} target="_blank" rel="noreferrer">PDF</a></>}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {session && ledger.length > 0 && (
        <Card title="Mouvements du solde" className="mt-6">
          <Table head={['Date', 'Type', 'Signatures', 'Détail']}>
            {ledger.map((l) => (
              <tr key={l.id}>
                <Td className="whitespace-nowrap">{dateTime(l.created_at)}</Td>
                <Td><Badge tone={l.kind === 'purchase' ? 'green' : l.kind === 'grant' ? 'blue' : 'amber'}>{KIND[l.kind]}</Badge></Td>
                <Td className={`tabular-nums font-medium ${l.credits < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{l.credits > 0 ? `+${l.credits}` : l.credits}</Td>
                <Td className="text-slate-500">{[l.reference, l.note].filter(Boolean).join(' · ') || '—'}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </div>
  );
}
